/**
 * Chain / network reference for the Documentation docupedia.
 * Public background is paraphrased from Wikipedia (linked). Product notes are LE-Workbench specific.
 */

export interface ChainDocEntry {
    id: string;
    /** Workbench chain code */
    code: string;
    title: string;
    summary: string;
    /** Why this chain matters for Indian LE investigations */
    leRelevance: string;
    /** How officers use it in CryptoFlow */
    howInProduct: string;
    addressHints: string;
    wikipediaUrl: string;
    /** Short attribution line */
    sourceNote: string;
    tags: string[];
}

export const DOCUMENTATION_CHAINS: ChainDocEntry[] = [
    {
        id: 'tron',
        code: 'TRX',
        title: 'Tron (TRX)',
        summary:
            'Tron is a proof-of-stake smart-contract blockchain. Its native coin is Tronix (TRX). It launched its own mainnet in 2018 after starting as an Ethereum token. Token standards on Tron include TRC-20 (used heavily for USDT).',
        leRelevance:
            'USDT on Tron (USDT-TRC20) is the dominant rail in many India-facing crypto fraud / P2P cash-out cases. Addresses are base58 and start with T — case must be preserved (never force-lowercase). Public reporting has repeatedly flagged Tron as heavily used in illicit USDT movement in Asia; treat that as investigative context, not automatic guilt for every address.',
        howInProduct:
            'Paste a T… address or TRC-20 tx hash into the Omnibar. Workbench auto-detects TRX, loads TronGrid-backed transactions via GraphService, and keeps address casing intact. Prefer USDT-TRC20 edges when reading path values.',
        addressHints: '34-character base58, starts with T. Example shape: TWd4WrZ9…',
        wikipediaUrl: 'https://en.wikipedia.org/wiki/Tron_(blockchain)',
        sourceNote: 'Background paraphrased from Wikipedia “Tron (blockchain)”; LE notes are product guidance.',
        tags: ['TRX', 'TRC20', 'USDT', 'India', 'P2P'],
    },
    {
        id: 'ethereum',
        code: 'ETH',
        title: 'Ethereum (ETH)',
        summary:
            'Ethereum is a decentralized blockchain with smart-contract functionality. Ether (ETH) is the native cryptocurrency. Most EVM “alt” chains share the same address format (0x…) and tooling patterns.',
        leRelevance:
            'ETH and ERC-20 tokens appear in mixers (e.g. Tornado Cash historical exposure), DEX swaps, and cross-chain bridges. OFAC-listed contracts and sanctioned entities are frequently Ethereum-native.',
        howInProduct:
            'EVM chains route through Etherscan V2 (`chainid`). Omnibar accepts 0x addresses and tx hashes. Inspector Intel tab surfaces sanctions, DEX swaps, and bridging activity when data is available.',
        addressHints: '0x-prefixed hex, 42 characters. Case-insensitive for matching; product normalizes EVM addresses to lowercase.',
        wikipediaUrl: 'https://en.wikipedia.org/wiki/Ethereum',
        sourceNote: 'Background paraphrased from Wikipedia “Ethereum”; LE notes are product guidance.',
        tags: ['ETH', 'ERC20', 'EVM', 'DEX', 'sanctions'],
    },
    {
        id: 'bitcoin',
        code: 'BTC',
        title: 'Bitcoin (BTC)',
        summary:
            'Bitcoin is the first decentralized cryptocurrency, introduced in 2009. It is pseudonymous: addresses and amounts are public, but real-world identity requires off-chain correlation.',
        leRelevance:
            'BTC remains common for ransom, darknet markets, and long peel chains. Clustering heuristics and exchange cash-out hops matter. Railway IPs can be rate-limited by Blockstream — warm cache via triage before deep investigation.',
        howInProduct:
            'Paste a BTC address or tx hash. Trace depth for triage auto-trace can go deeper on BTC than other chains. Preserve address case (Bech32 / base58 variants).',
        addressHints: 'Legacy 1…/3… or Bech32 bc1… formats. Do not lowercase Bech32.',
        wikipediaUrl: 'https://en.wikipedia.org/wiki/Bitcoin',
        sourceNote: 'Background paraphrased from Wikipedia “Bitcoin”; LE notes are product guidance.',
        tags: ['BTC', 'peel-chain', 'clustering'],
    },
    {
        id: 'usdt',
        code: 'USDT',
        title: 'Tether (USDT)',
        summary:
            'Tether (USDT) is a dollar-pegged stablecoin issued across multiple blockchains. On Tron it uses TRC-20; on Ethereum it uses ERC-20. The token is not a chain by itself — always name the rail (e.g. USDT-TRC20).',
        leRelevance:
            'Most India-facing fraud cash-outs prefer USDT because value is stable and P2P desks settle against UPI. Writing “USDT” without the chain in an FIR or LE Report is incomplete evidence.',
        howInProduct:
            'Path enrichment groups edges by token and prefers stablecoins when labeling gold trails. Reports show primary token plus secondary_tokens so officers do not sum USDT + TRX into one fake total.',
        addressHints: 'Look at the chain of the holding address (T… vs 0x…), not the word USDT alone.',
        wikipediaUrl: 'https://en.wikipedia.org/wiki/Tether_(cryptocurrency)',
        sourceNote: 'Public stablecoin background; LE notes are product guidance.',
        tags: ['USDT', 'stablecoin', 'TRC20', 'ERC20'],
    },
    {
        id: 'bsc',
        code: 'BSC',
        title: 'BNB Smart Chain (BSC)',
        summary:
            'BNB Smart Chain is an EVM-compatible chain in the Binance ecosystem. Addresses look like Ethereum (0x…). Many scam tokens and DEX rugs launch here because fees are low and tooling is familiar.',
        leRelevance:
            'Useful when a victim bought a token on PancakeSwap-class DEXes or when funds hop ETH ↔ BSC via bridges before India cash-out.',
        howInProduct:
            'Select BSC (or paste 0x and confirm chain badge). Same Inspector / Auto-Investigate flow as other EVM chains via Etherscan V2 chainid.',
        addressHints: 'Same 0x format as Ethereum — always confirm the chain badge before tracing.',
        wikipediaUrl: 'https://en.wikipedia.org/wiki/Binance#BNB_Smart_Chain',
        sourceNote: 'Public EVM-chain background; LE notes are product guidance.',
        tags: ['BSC', 'EVM', 'DEX', 'rug'],
    },
    {
        id: 'polygon',
        code: 'POLYGON',
        title: 'Polygon (MATIC / POL)',
        summary:
            'Polygon is an EVM sidechain / scaling network for Ethereum-compatible apps. Addresses are 0x…. Fees are typically lower than mainnet Ethereum.',
        leRelevance:
            'Appears when funds bridge off Ethereum or when scam dApps target low-fee networks. Treat as a separate hop in multi-chain trails.',
        howInProduct:
            'Confirm chain in Omnibar. Bridging Activity in Intel tab helps spot ETH ↔ Polygon hops when enrichment returns bridge data.',
        addressHints: '0x format — do not assume ETH mainnet without checking the badge.',
        wikipediaUrl: 'https://en.wikipedia.org/wiki/Polygon_(blockchain)',
        sourceNote: 'Public network background; LE notes are product guidance.',
        tags: ['POLYGON', 'EVM', 'bridge'],
    },
];
